import {createRow} from 'antibodies/nested-data-creation/full-screen/ProteinCreationTableUtil';
import {
  ProteinCreationCellValueType,
  ProteinCreationRow,
} from 'antibodies/nested-data-creation/full-screen/ProteinCreationTypes';
import type {StructuredTableColumn} from 'notebook/structured-tables/StructuredTableConstants';
import {IGNORE_FIELD_ID} from 'spreadsheet-import-wizard/shared/ColumnMapper';
import {getNormalizedName, matchByNameTokens} from 'spreadsheet-import-wizard/shared/matchByNameTokens';

/**
 * Map spreadsheet headers to table columns by name with `matchByNameTokens`, so headers that differ from a
 * column name only in casing or separators, or that share enough words with it, are pre-mapped. Columns whose
 * name equals another column's, ignoring casing and separators, are never matched, since there is no way to
 * tell which one a header means. Headers with no confident match are left unmapped.
 */
export function getHeaderMappingsByName(
  headers: ReadonlyArray<string>,
  columns: ReadonlyArray<StructuredTableColumn>
): Record<string, string> {
  const columnCountByNormalizedName = new Map<string, number>();
  for (const column of columns) {
    const normalizedName = getNormalizedName(column.name);
    columnCountByNormalizedName.set(
      normalizedName,
      (columnCountByNormalizedName.get(normalizedName) ?? 0) + 1
    );
  }
  const uniquelyNamedColumns = columns.filter(
    (column) => columnCountByNormalizedName.get(getNormalizedName(column.name)) === 1
  );

  return Object.fromEntries(
    matchByNameTokens(uniquelyNamedColumns, headers, {
      getTargetName: (column) => column.name,
      getSourceName: (header) => header,
    }).map(({target, source}) => [source, target.provenanceUuid])
  );
}

export function areAllHeadersMapped(
  headers: ReadonlyArray<string>,
  mappings: Record<string, string>
): boolean {
  return headers.every((header) => mappings[header] != null);
}

/**
 * Convert parsed spreadsheet rows into table rows, filling each column from its mapped header. Columns
 * without a mapped header are left empty.
 */
export function convertSpreadsheetRowsToProteinCreationRows({
  columns,
  mappings,
  rows,
}: {
  columns: ReadonlyArray<StructuredTableColumn>;
  /** Spreadsheet header to column `provenanceUuid`, or `IGNORE_FIELD_ID`. */
  mappings: Record<string, string>;
  rows: ReadonlyArray<Record<string, string>>;
}): Array<ProteinCreationRow> {
  const sourceHeaderByColumnId = new Map(
    Object.entries(mappings).flatMap(([header, columnId]) =>
      columnId === IGNORE_FIELD_ID ? [] : [[columnId, header]]
    )
  );

  return rows.map((row) =>
    createRow(columns, (column) => {
      const sourceHeader = sourceHeaderByColumnId.get(column.provenanceUuid);
      const text = sourceHeader != null ? (row[sourceHeader] ?? '') : '';
      return text !== ''
        ? {type: ProteinCreationCellValueType.RAW_TEXT, text}
        : {type: ProteinCreationCellValueType.EMPTY};
    })
  );
}
